import { useInfiniteQuery } from "react-query";
import { getOrders } from "../../api/orders";
import { transformOrdersSchema } from "../../helpers";
import { SummaryOrderRow } from "../Rows/SummaryOrderRow";
import { PiPackage } from "react-icons/pi";

function getDateLabel(dateString) {
  const date = new Date(dateString);

  const today = new Date();
  const yesterday = new Date();

  today.setHours(0, 0, 0, 0);
  yesterday.setDate(today.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);

  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);

  if (normalizedDate.getTime() === today.getTime()) {
    return "Today";
  }

  if (normalizedDate.getTime() === yesterday.getTime()) {
    return "Yesterday";
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/* ---------------------------------------------
   Skeleton loading state
--------------------------------------------- */
function RecentActivitySkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-3">
          <div className="h-4 w-28 bg-gray-200 rounded" />
          <div className="space-y-2">
            {[1, 2].map((j) => (
              <div key={j} className="h-14 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------
   Empty state
--------------------------------------------- */
function NoOrders() {
  return (
    <div
      className="flex flex-col items-center justify-center py-20 
                 rounded-xl border border-gray-200 bg-white
                 text-center"
    >
      <div
        className="w-16 h-16 rounded-xl 
                   bg-gradient-to-br from-[#0052FF]/10 to-[#0099FF]/10
                   flex items-center justify-center mb-4"
      >
        <PiPackage className="w-8 h-8 text-[#0052FF]" />
      </div>

      <h3 className="text-gray-900 font-semibold text-lg">
        No Orders Yet
      </h3>

      <p className="text-gray-500 text-sm mt-1 max-w-xs">
        Once you create a shipment, it will appear here for quick access and tracking.
      </p>
    </div>
  );
}

/* ---------------------------------------------
   Main component
--------------------------------------------- */
export default function RecentActivityTable() {
  const status = ["CONFIRMED", "CANCELLED"];

  const { data, isLoading, isError } = useInfiniteQuery(
    ["orders", status],
    ({ pageParam }) => getOrders({ pageParam, status, limit: 10 }),
    {
      getNextPageParam: (lastPage) =>
        lastPage.lastEvaluatedKey || undefined,
      retry: 0,
    }
  );

  if (isLoading) return <RecentActivitySkeleton />;

  if (isError) {
    return (
      <div className="text-red-500 text-center py-10">
        Failed to load orders. Please try again later.
      </div>
    );
  }

  const allOrders =
    data?.pages.flatMap((page) => page.items) || [];

  if (allOrders.length === 0) return <NoOrders />;

  return (
    <div className="space-y-6">
      {data?.pages.map((group) => {
        const ordersMap = transformOrdersSchema(group?.items);
        const entries = Object.entries(ordersMap);

        return entries.map(([date, orders]) => {
          const label = getDateLabel(date);
          return (
            <div key={date} className="space-y-3">
              {/* Date header */}
              <div className="flex items-center gap-3">
                <span
                  className={`text-xs font-medium uppercase tracking-wider ${
                    label === "Today"
                      ? "text-blue"
                      : label === "Yesterday"
                      ? "text-blue"
                      : "text-gray-500"
                  }`}
                >
                  {label}
                </span>
                <div className="flex-1 border-t border-gray-200" />
              </div>

              {/* Orders */}
              <div className="space-y-4">
                {orders.map((order) => (
                  <SummaryOrderRow
                    key={order.orderId}
                    order={order}
                  />
                ))}
              </div>
            </div>
          );
        });
      })}
    </div>
  );
}
